// Параллакс картинок в плитках: при прокрутке фото едет внутри своей рамки медленнее
// страницы. Рамка (.component-tile-picture) режет лишнее по overflow, а запас на сдвиг
// даёт базовый scale картинки — он задан в main.scss переменной --tile-parallax-scale.
//
// Позицию берём из getBoundingClientRect рамки, а не из window.scrollY: контент на
// главной двигает виджет smoothScroll трансформом, и scrollY уезжает вперёд картинки.
// Саму рамку никто не трансформирует, так что её прямоугольник — честное место на
// экране, кто бы её туда ни привёз.
//
// Сдвиг пишется отдельным свойством translate, а не в transform: зум по наведению
// живёт в scale с CSS-переходом, и в общем transform они бы затирали друг друга.

// Какая часть запаса идёт в дело: остаток — страховка от округлений и субпикселей
const SHIFT_SAFETY = 0.85;
// Столько кадров без движения ждём, прежде чем погасить цикл
const IDLE_FRAMES = 20;
// Сдвиг мельче — не пишем, незачем дёргать стиль на доли пикселя
const MIN_STEP = 0.05;
// Насколько выше и ниже экрана продолжаем считать: иначе плитка въезжает уступом
const VIEWPORT_MARGIN = 100;

// Цикл на все плитки страницы один, поэтому список общий для всех вызовов виджета
const frames = [];
let animationId = null;
let idleFrames = 0;
let lastReference = null;
let listening = false;

export default function tilesParallax(element) {
    // Прямая просьба не анимировать
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
    }

    const scale = Number(getComputedStyle(element).getPropertyValue("--tile-parallax-scale"));

    // Без запаса двигать нечего: картинка сразу оголит край рамки
    if (!(scale > 1)) {
        return;
    }

    element.querySelectorAll(".component-tile-picture").forEach(function (frame) {
        const image = frame.querySelector("img");

        if (image) {
            frames.push({ frame, image, scale, amplitude: 0, shift: null, top: 0, next: 0, visible: false });
        }
    });

    measureAll();
    listen();
    start();

    // Высота плиток меняется от ширины окна, шрифтов и догрузки картинок
    if (window.ResizeObserver) {
        new ResizeObserver(function () {
            measureAll();
            start();
        }).observe(element);
    }
}

function measureAll() {
    frames.forEach(function (entry) {
        // offsetHeight, а не rect: у картинки в rect уже учтён её собственный scale.
        // Запас с каждой стороны — половина того, на сколько увеличенная картинка
        // выходит за рамку; если она вдруг ниже рамки, запас честно уйдёт в ноль
        const overhang = (entry.image.offsetHeight * entry.scale - entry.frame.offsetHeight) / 2;

        entry.amplitude = Math.max(overhang, 0) * SHIFT_SAFETY;
    });
}

function animate() {
    const viewport = window.innerHeight;
    let moved = false;

    // Сперва все замеры, потом все записи: вперемешку браузер пересчитывает
    // раскладку по нескольку раз за кадр
    frames.forEach(function (entry) {
        const rect = entry.frame.getBoundingClientRect();

        entry.top = rect.top;
        entry.visible = rect.bottom > -VIEWPORT_MARGIN && rect.top < viewport + VIEWPORT_MARGIN;

        if (entry.visible) {
            // 0 — плитка только показалась снизу, 1 — только ушла за верх
            const progress = (viewport - rect.top) / (viewport + rect.height);

            entry.next = (Math.min(Math.max(progress, 0), 1) - 0.5) * 2 * entry.amplitude;
        }
    });

    frames.forEach(function (entry) {
        if (!entry.visible || (entry.shift !== null && Math.abs(entry.next - entry.shift) < MIN_STEP)) {
            return;
        }

        entry.shift = entry.next;
        entry.image.style.translate = "0 " + entry.next.toFixed(2) + "px";
    });

    // Едет ли страница, спрашиваем у первой рамки, а не у сдвигов: плитки могут быть
    // далеко за экраном, пока контент ещё догоняет скроллбар, — гасить цикл рано
    if (frames.length) {
        moved = lastReference === null || Math.abs(frames[0].top - lastReference) > MIN_STEP;
        lastReference = frames[0].top;
    }

    idleFrames = moved ? 0 : idleFrames + 1;

    if (idleFrames > IDLE_FRAMES) {
        stop();
        return;
    }

    animationId = requestAnimationFrame(animate);
}

function start() {
    idleFrames = 0;

    if (animationId === null) {
        animationId = requestAnimationFrame(animate);
    }
}

function stop() {
    if (animationId !== null) {
        cancelAnimationFrame(animationId);
        animationId = null;
    }
}

function listen() {
    if (listening) {
        return;
    }

    listening = true;

    window.addEventListener("scroll", start, { passive: true });
    window.addEventListener("resize", function () {
        measureAll();
        start();
    });
}
